import i18n from 'i18next';
import {showNotification} from 'platform/components';

import {isNil} from 'ramda';

import type {BulkOperationResponseBody} from '../types/api/bulkOperations';
import type {BulkOperationsServices} from '../types/BulkOperationsServices';
import {createPendingBulkOperation} from '../utils/createPendingBulkOperation';
import {normalizeBulkOperationError} from '../utils/normalizeBulkOperationError';
import {usePendingBulkOperationRequests} from './usePendingBulkOperationRequests';

const handleApiError = () =>
  showNotification.error(i18n.t('general.notifications.defaultErrorMessage'));

/** Owns reruns for the tracking lifetime, independently of panel and dialog visibility. */
export function useBulkOperationRecovery(args: {
  services: Pick<BulkOperationsServices, 'useRerunFailed' | 'handleApiError'>;
  onRegistered: (operation: BulkOperationResponseBody, sourceOperationId: string) => void;
}) {
  const [submit] = args.services.useRerunFailed();
  const requests = usePendingBulkOperationRequests({
    request: (operation: BulkOperationResponseBody) =>
      submit({bulkOperationId: operation.bulkOperationId}).unwrap(),
    onSuccess: (operation, response) => {
      if (isNil(response)) {
        return;
      }
      args.onRegistered(
        createPendingBulkOperation({
          actionCode: operation.actionCode,
          bulkOperationId: response.bulkOperationId,
          resourceId: operation.resourceId,
          totalCount: operation.failedCount,
        }),
        operation.bulkOperationId
      );
    },
    onError: (_operation, error) =>
      (args.services.handleApiError ?? handleApiError)(normalizeBulkOperationError(error)),
  });

  return {pendingIds: requests.pendingIds, rerun: requests.start};
}
